import React, { useState, useMemo } from 'react';
import { Download, Upload, FileSpreadsheet, FileText, Info, Eye } from 'lucide-react';
import Button from '../common/Button';
import ImportModal from './modals/ImportModal';
import ExportConfigModal from './modals/ExportConfigModal';
import { useToastContext } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { canImportExport } from '../../utils/permissions';

const AttendeeImportExport = ({ 
  selectedEvent, 
  attendees = [], 
  onImportComplete,
  className = '' 
}) => {
  const { user } = useAuth();
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showColumnPreview, setShowColumnPreview] = useState(false);
  const toast = useToastContext();

  // Check if user has import/export permissions
  const hasImportExportPermission = canImportExport(user);

  // Generate column headers preview for users
  const columnHeaders = useMemo(() => {
    if (!attendees || attendees.length === 0) return [];
    
    const customFieldsMap = new Map();
    
    // Collect all custom fields with their labels
    attendees.forEach(attendee => {
      attendee.data?.forEach(fieldData => {
        const fieldName = fieldData.field?.field_name || fieldData.custom_field?.field_name;
        const fieldLabel = fieldData.field?.field_label || fieldData.custom_field?.field_label || fieldName;
        
        if (fieldName && !customFieldsMap.has(fieldName)) {
          customFieldsMap.set(fieldName, fieldLabel);
        }
      });
    });

    const headers = [];
    
    // Add custom fields first (main data)
    customFieldsMap.forEach((label, fieldName) => {
      headers.push({ type: 'custom', name: fieldName, label, description: 'Attendee information' });
    });
    
    // Add standard fields (excluding ID and Last Updated)
    const standardFields = [
      { key: 'registration_number', header: 'Registration Number', description: 'Event registration number' },
      { key: 'created_at', header: 'Registration Date', description: 'When attendee registered' },
      { key: 'event', header: 'Event Name', description: 'Associated event name' }
    ];
    
    standardFields.forEach(({ key, header, description }) => {
      if (attendees.some(attendee => attendee[key] !== undefined)) {
        headers.push({ type: 'standard', name: key, label: header, description });
      }
    });
    
    return headers;
  }, [attendees]);

  // Helper function to get attendee field value
  const getFieldValue = (attendee, fieldName) => {
    // Check if it's in the data array
    const dataField = attendee.data?.find(d => 
      d.field?.field_name === fieldName || 
      d.custom_field?.field_name === fieldName
    );
    if (dataField) {
      return dataField.value || dataField.field_value || '';
    }
    // Check direct properties
    return attendee[fieldName] || '';
  };

  // Client-side CSV export
  const exportToCSV = (data, filename) => {
    if (data.length === 0) return;

    // Create a comprehensive field mapping with proper headers
    const customFieldsMap = new Map();
    
    // First pass: collect all custom fields with their labels
    data.forEach(attendee => {
      attendee.data?.forEach(fieldData => {
        const fieldName = fieldData.field?.field_name || fieldData.custom_field?.field_name;
        const fieldLabel = fieldData.field?.field_label || fieldData.custom_field?.field_label || fieldName;
        
        if (fieldName && !customFieldsMap.has(fieldName)) {
          customFieldsMap.set(fieldName, fieldLabel);
        }
      });
    });

    // Define standard fields with proper headers (excluding ID and Last Updated)
    const standardFields = [
      { key: 'registration_number', header: 'Registration Number' },
      { key: 'created_at', header: 'Registration Date' },
      { key: 'event', header: 'Event Name' }
    ];

    // Build complete headers array
    const headers = [];
    const fieldKeys = [];
    
    // Add custom fields first (these are the main data)
    customFieldsMap.forEach((label, fieldName) => {
      headers.push(label);
      fieldKeys.push({ type: 'custom', key: fieldName });
    });
    
    // Add standard fields
    standardFields.forEach(({ key, header }) => {
      if (data.some(attendee => attendee[key] !== undefined)) {
        headers.push(header);
        fieldKeys.push({ type: 'standard', key });
      }
    });
    
    // Debug logging for troubleshooting
    console.log('🔍 CSV Export Debug:', {
      totalAttendees: data.length,
      customFieldsFound: customFieldsMap.size,
      customFields: Array.from(customFieldsMap.entries()),
      headers: headers,
      sampleAttendeeData: data[0]?.data
    });

    // Create CSV content with proper headers
    let csvContent = headers.map(h => `"${h}"`).join(',') + '\n';
    
    data.forEach(attendee => {
      const row = fieldKeys.map(({ type, key }) => {
        let value = '';
        
        if (type === 'custom') {
          // Get custom field value
          value = getFieldValue(attendee, key);
        } else {
          // Get standard field value
          switch (key) {
            case 'created_at':
            case 'updated_at':
              value = attendee[key] ? new Date(attendee[key]).toLocaleString() : '';
              break;
            case 'event':
              value = attendee.event?.name || '';
              break;
            default:
              value = attendee[key] || '';
          }
        }
        
        // Ensure value is a string and escape quotes
        value = String(value || '').replace(/"/g, '""');
        return `"${value}"`;
      });
      csvContent += row.join(',') + '\n';
    });

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Client-side Excel export (using CSV format with .xlsx extension for simplicity)
  const exportToExcel = (data, filename) => {
    // For true Excel support, you'd need a library like xlsx
    // For now, we'll use CSV format which Excel can open
    exportToCSV(data, filename.replace('.xlsx', '.csv'));
  };

  // Frontend export (primary and only method)
  const handleQuickExport = async (format = 'csv') => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }

    if (attendees.length === 0) {
      toast.error('No attendees to export');
      return;
    }

    setIsExporting(true);
    
    try {
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `attendees-export-${timestamp}.${format}`;
      
      if (format === 'csv') {
        exportToCSV(attendees, filename);
      } else if (format === 'xlsx') {
        exportToExcel(attendees, filename);
      }
      
      toast.success(`Exported ${attendees.length} attendees successfully!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export attendees: ' + (error.message || 'Unknown error'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportComplete = (result) => {
    setShowImportModal(false);
    
    if (result.success) {
      // Show success message with details
      const successMsg = `Successfully imported ${result.imported} attendees!`;
      if (result.errors > 0 || result.skipped > 0) {
        const details = [];
        if (result.errors > 0) details.push(`${result.errors} errors`);
        if (result.skipped > 0) details.push(`${result.skipped} skipped`);
        toast.success(`${successMsg} (${details.join(', ')})`);
      } else {
        toast.success(successMsg);
      }
      
      // Show warnings if any
      if (result.warnings?.length > 0) {
        console.log('Import warnings:', result.warnings);
        // Show first few warnings in a toast
        const warningMsg = result.warnings.slice(0, 3).join('. ');
        toast.warning(`Import completed with issues: ${warningMsg}`);
      }
      
      onImportComplete?.();
    } else {
      toast.error(result.message || 'Import failed');
      console.error('Import failed:', result);
    }
  };

  if (!selectedEvent) {
    return null; // Don't show buttons if no event selected
  }

  // Don't show buttons if user doesn't have import/export permission
  if (!hasImportExportPermission) {
    return null;
  }

  return (
    <>
      <div className={`flex gap-2 ${className}`}>
        {/* Import Button */}
        <Button
          variant="secondary"
          onClick={() => setShowImportModal(true)}
          className="gap-2"
          disabled={!selectedEvent}
        >
          <Upload className="h-4 w-4" />
          Import
        </Button>

        {/* Export Dropdown */}
        <div className="relative group">
          <Button
            variant="secondary"
            onClick={() => handleQuickExport('csv')}
            className="gap-2"
            disabled={!selectedEvent || attendees.length === 0 || isExporting}
            loading={isExporting}
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
          
          {/* Export Options Dropdown */}
          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
            <div className="py-1">
              <button
                onClick={() => handleQuickExport('csv')}
                disabled={isExporting}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <FileText className="h-4 w-4" />
                Export as CSV
              </button>
              <button
                onClick={() => handleQuickExport('xlsx')}
                disabled={isExporting}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Export as Excel
              </button>
              <hr className="my-1" />
              <button
                onClick={() => setShowExportModal(true)}
                disabled={isExporting}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                Advanced Export...
              </button>
              <hr className="my-1" />
              <button
                onClick={() => setShowColumnPreview(true)}
                disabled={!selectedEvent || attendees.length === 0}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <Eye className="h-4 w-4" />
                Preview Columns
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        selectedEvent={selectedEvent}
        onImportComplete={handleImportComplete}
      />

      {/* Export Configuration Modal */}
      <ExportConfigModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        selectedEvent={selectedEvent}
        attendees={attendees}
        onExportComplete={(result) => {
          setShowExportModal(false);
          if (result.success) {
            const fieldsText = result.fieldsCount ? ` with ${result.fieldsCount} fields` : '';
            toast.success(`Exported ${result.count} attendees${fieldsText} successfully!`);
          }
        }}
      />

      {/* Column Preview Modal */}
      {showColumnPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Export Column Preview</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    These are the column headers that will appear in your exported CSV/Excel file
                  </p>
                </div>
                <button
                  onClick={() => setShowColumnPreview(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Summary */}
              <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{columnHeaders.length}</div>
                  <div className="text-sm text-blue-800">Total Columns</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {columnHeaders.filter(h => h.type === 'custom').length}
                  </div>
                  <div className="text-sm text-green-800">Custom Fields</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {columnHeaders.filter(h => h.type === 'standard').length}
                  </div>
                  <div className="text-sm text-purple-800">System Fields</div>
                </div>
              </div>

              {/* Column List */}
              <div className="space-y-4">
                {/* Custom Fields Section */}
                {columnHeaders.filter(h => h.type === 'custom').length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      Custom Fields (Attendee Data)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {columnHeaders
                        .filter(h => h.type === 'custom')
                        .map((header, index) => (
                          <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="font-medium text-green-900">{header.label}</div>
                            <div className="text-sm text-green-700 mt-1">
                              Field: {header.name}
                            </div>
                            <div className="text-xs text-green-600 mt-1">
                              {header.description}
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}

                {/* System Fields Section */}
                {columnHeaders.filter(h => h.type === 'standard').length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                      System Fields (Metadata)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {columnHeaders
                        .filter(h => h.type === 'standard')
                        .map((header, index) => (
                          <div key={index} className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <div className="font-medium text-purple-900">{header.label}</div>
                            <div className="text-sm text-purple-700 mt-1">
                              Field: {header.name}
                            </div>
                            <div className="text-xs text-purple-600 mt-1">
                              {header.description}
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}
              </div>

              {/* Usage Tips */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h5 className="font-medium text-blue-900 mb-2">Import/Export Tips</h5>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Use these exact column names when importing data back into the system</li>
                      <li>• Custom fields contain the main attendee information (names, emails, etc.)</li>
                      <li>• System fields contain metadata and are automatically generated</li>
                      <li>• Column order in exports: Custom fields first, then system fields</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <Button
                  variant="secondary"
                  onClick={() => setShowColumnPreview(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AttendeeImportExport;
